import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, apiScope } from '../auth/msalConfig.js';

/**
 * Returns a valid MSAL ID token for the BFF API.
 *
 * Authn is enforced at the edge (Kong / APIM), which validates the token's
 * signature and audience (the UI's own client ID) before the bff is invoked.
 * The bff itself only decodes the token to read identity/roles — it does NOT
 * re-verify it. There is no separate api://<app> resource to request an access
 * token for; the ID token's audience is this app's own client ID.
 *
 * Tries a silent acquire first. If no account is found, or if interaction is
 * required (session expired, consent needed, etc.), it initiates a redirect
 * and then throws so the in-flight request does not proceed. The redirect will
 * eventually land back in the app and re-trigger the original navigation.
 */
/**
 * Best-effort ID token for background telemetry (see
 * telemetry/reportClientError.ts). Unlike getBffToken this is completely
 * passive: it never starts an interactive login and never throws — a failure
 * to report an error must not redirect the user to the IdP. Returns null when
 * there is no usable session, in which case the caller drops the report.
 */
export async function getBffTokenSilent(): Promise<string | null> {
  try {
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) {
      return null;
    }
    const result = await msalInstance.acquireTokenSilent({ account, scopes: apiScope });
    return result.idToken;
  } catch {
    return null;
  }
}

export async function getBffToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

  if (!account) {
    // No cached session at all — force an interactive login.
    await msalInstance.acquireTokenRedirect({ scopes: apiScope });
    throw new Error('no-account: redirecting for authentication');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: apiScope,
    });
    return result.idToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Interaction required: navigate to the IdP. Other errors (network,
      // CORS, transient) must propagate so callers can retry/handle them
      // instead of bouncing the user out to re-authenticate.
      await msalInstance.acquireTokenRedirect({ scopes: apiScope });
    }
    throw err;
  }
}
